import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, RefreshCw, Download, Mail, AlertTriangle, List } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const ROTULO_GRUPO = { mensal: 'Mensal', bimestral: 'Bimestral', trimestral: 'Trimestral' };

// Abaixo disso, o aviso no modal de recálculo muda de cor pra chamar mais atenção —
// o cron semanal roda todo domingo, então 20h já cobre uma janela de "calculado há pouco".
const HORAS_CALCULO_RECENTE = 20;

function formatarDataHora(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function horasDesde(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60);
}

export default function EstoqueMinimoIdeal() {
  const navigate = useNavigate();
  const { isGerenteOuAdmin } = useAuth();
  const admin = isGerenteOuAdmin();

  const [linhas, setLinhas] = useState([]);
  const [edicoes, setEdicoes] = useState({});
  const [salvandoCodigo, setSalvandoCodigo] = useState(null);
  const [recalculandoCodigo, setRecalculandoCodigo] = useState(null);
  const [sucessoCodigo, setSucessoCodigo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');

  const [periodoBusca, setPeriodoBusca] = useState(6);
  const [calculando, setCalculando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [enviandoAlertas, setEnviandoAlertas] = useState(false);
  const [mostrarModalCalcular, setMostrarModalCalcular] = useState(false);

  const [statusCalculo, setStatusCalculo] = useState(null);
  const [calculandoBusca, setCalculandoBusca] = useState(false);

  const [parametros, setParametros] = useState(null);
  const [edicoesParametros, setEdicoesParametros] = useState({});
  const [salvandoParametros, setSalvandoParametros] = useState(false);
  const [mostrarParametros, setMostrarParametros] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      // Só mensal na tela: carregar todos os grupos de uma vez trava o navegador (muitos
      // produtos). Bimestral/trimestral ficam só na planilha exportada (.xlsx).
      const resp = await apiService.estoqueMinimoIdeal.listar({ grupoFinal: 'mensal' });
      setLinhas(resp.data.data ?? []);
    } catch {
      setErro('Erro ao carregar dados de estoque mínimo/ideal. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarParametros = useCallback(async () => {
    try {
      const resp = await apiService.estoqueMinimoIdeal.obterParametros();
      setParametros(resp.data.data ?? null);
    } catch {
      // Falha silenciosa — a seção de parâmetros mostra "Carregando..." indefinidamente,
      // o que já sinaliza o problema sem duplicar o banner de erro da listagem principal.
    }
  }, []);

  const carregarStatusCalculo = useCallback(async () => {
    try {
      const resp = await apiService.estoqueMinimoIdeal.status();
      setStatusCalculo(resp.data.data ?? null);
    } catch {
      // Falha silenciosa — vira só a ausência do aviso "último cálculo", não é crítico.
    }
  }, []);

  // Carrega automaticamente ao abrir a página: parâmetros, status do último cálculo, e os
  // produtos mensais já calculados (só mensal — não mexe no ERP, é MariaDB local; o filtro
  // por grupo é justamente o que evita travar o navegador com o volume total de produtos).
  useEffect(() => {
    carregarParametros();
    carregarStatusCalculo();
    carregar();
  }, [carregarParametros, carregarStatusCalculo, carregar]);

  function valorAtual(linha, campo) {
    const edicao = edicoes[linha.codigo_produto];
    if (edicao && edicao[campo] !== undefined) return edicao[campo];
    return linha[campo];
  }

  function handleChange(codigoProduto, campo, valor) {
    setEdicoes(prev => ({
      ...prev,
      [codigoProduto]: { ...prev[codigoProduto], [campo]: valor },
    }));
  }

  function temAlteracao(linha) {
    const edicao = edicoes[linha.codigo_produto];
    if (!edicao) return false;
    return Object.keys(edicao).some(campo => String(edicao[campo] ?? '') !== String(linha[campo] ?? ''));
  }

  async function handleSalvar(linha) {
    const edicao = edicoes[linha.codigo_produto];
    if (!edicao) return;

    setSalvandoCodigo(linha.codigo_produto);
    setErro('');
    try {
      const dados = { ...edicao };
      if (dados.lead_time_dias !== undefined) {
        dados.lead_time_dias = dados.lead_time_dias === '' ? null : Number(dados.lead_time_dias);
      }
      if (dados.margem_seguranca_dias !== undefined) {
        dados.margem_seguranca_dias = dados.margem_seguranca_dias === '' ? null : Number(dados.margem_seguranca_dias);
      }
      if (dados.gmm_estimado_manual !== undefined) {
        dados.gmm_estimado_manual = dados.gmm_estimado_manual === '' ? null : Number(dados.gmm_estimado_manual);
      }
      if (dados.grupo_final !== undefined) {
        dados.grupo_final = dados.grupo_final === '' ? null : dados.grupo_final;
      }

      const resp = await apiService.estoqueMinimoIdeal.atualizar(linha.codigo_produto, dados);
      const atualizado = resp.data.data;
      setLinhas(prev => prev.map(l => (l.codigo_produto === linha.codigo_produto ? atualizado : l)));
      setEdicoes(prev => {
        const { [linha.codigo_produto]: _omitido, ...resto } = prev;
        return resto;
      });
      setSucessoCodigo(linha.codigo_produto);
      setTimeout(() => setSucessoCodigo(null), 2000);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar produto.');
    } finally {
      setSalvandoCodigo(null);
    }
  }

  async function handleCalcular() {
    setMostrarModalCalcular(false);
    setCalculando(true);
    setErro('');
    try {
      const resp = await apiService.estoqueMinimoIdeal.recalcularTodos(periodoBusca);
      // Não carrega o resultado na tabela: com muitos produtos, renderizar tudo de uma
      // vez trava o navegador. Em vez disso, baixa a planilha pra visualização no Excel.
      await baixarPlanilha();
      await carregarStatusCalculo();
      toast.success(`Cálculo concluído: ${resp.data?.data?.totalProdutos ?? 0} produtos processados. Planilha baixada.`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao calcular estoque mínimo/ideal.');
    } finally {
      setCalculando(false);
    }
  }

  async function handleRecalcularProduto(linha) {
    setRecalculandoCodigo(linha.codigo_produto);
    setErro('');
    try {
      const resp = await apiService.estoqueMinimoIdeal.recalcularProduto(linha.codigo_produto, periodoBusca);
      const atualizado = resp.data.data;
      setLinhas(prev => prev.map(l => (l.codigo_produto === linha.codigo_produto ? atualizado : l)));
      toast.success(`"${linha.nome_produto || linha.codigo_produto}" recalculado`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao recalcular produto.');
    } finally {
      setRecalculandoCodigo(null);
    }
  }

  async function handleCalcularBusca() {
    const codigoProduto = busca.trim();
    if (!codigoProduto) return;

    setCalculandoBusca(true);
    setErro('');
    try {
      const resp = await apiService.estoqueMinimoIdeal.recalcularProduto(codigoProduto, periodoBusca);
      const atualizado = resp.data.data;
      setLinhas(prev => (prev.some(l => l.codigo_produto === atualizado.codigo_produto)
        ? prev.map(l => (l.codigo_produto === atualizado.codigo_produto ? atualizado : l))
        : [...prev, atualizado]));
      toast.success(`"${atualizado.nome_produto || codigoProduto}" calculado`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao calcular esse produto.');
    } finally {
      setCalculandoBusca(false);
    }
  }

  async function handleEnviarAlertas() {
    setEnviandoAlertas(true);
    try {
      const resp = await apiService.estoqueMinimoIdeal.enviarAlertas();
      const dados = resp.data?.data;
      if (dados?.enviado) {
        toast.success(`Email enviado: ${dados.totalProdutos} produto(s) abaixo do estoque mínimo`);
      } else {
        toast.success(dados?.motivo || 'Nenhum produto abaixo do estoque mínimo no momento.');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao enviar alertas por email.');
    } finally {
      setEnviandoAlertas(false);
    }
  }

  async function baixarPlanilha() {
    const resp = await apiService.estoqueMinimoIdeal.exportarXlsx();
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Estoque_Minimo_Ideal.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async function handleExportar() {
    setExportando(true);
    try {
      await baixarPlanilha();
    } catch {
      toast.error('Erro ao exportar planilha.');
    } finally {
      setExportando(false);
    }
  }

  function valorParametro(campo) {
    if (edicoesParametros[campo] !== undefined) return edicoesParametros[campo];
    return parametros ? parametros[campo] : '';
  }

  function handleChangeParametro(campo, valor) {
    setEdicoesParametros(prev => ({ ...prev, [campo]: valor }));
  }

  async function handleSalvarParametros() {
    setSalvandoParametros(true);
    try {
      const dados = { ...parametros, ...edicoesParametros };
      const resp = await apiService.estoqueMinimoIdeal.atualizarParametros(dados);
      setParametros(resp.data.data);
      setEdicoesParametros({});
      toast.success('Parâmetros salvos');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao salvar parâmetros.');
    } finally {
      setSalvandoParametros(false);
    }
  }

  const linhasFiltradas = linhas.filter(l => {
    if (!busca.trim()) return true;
    const termo = busca.trim().toLowerCase();
    return (l.nome_produto || '').toLowerCase().includes(termo) || String(l.codigo_produto).includes(termo);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Estoque Mínimo/Ideal</h1>
          <p className="text-sm text-gray-500">
            GMM, grupo de compra e níveis de estoque por produto (POP-EST-001)
            {!admin && ' (somente leitura)'}
          </p>
          {statusCalculo?.ultimoCalculo && (
            <p className="text-xs text-gray-400 mt-0.5">
              Último cálculo: {formatarDataHora(statusCalculo.ultimoCalculo)} · {statusCalculo.totalProdutos} produto(s)
            </p>
          )}
        </div>
      </div>

      {/* Toolbar: período de busca + calcular + exportar */}
      {admin && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-gray-200 rounded-xl px-4 py-3">
          <label className="text-sm text-gray-600">
            Período de busca de vendas:
            <select
              value={periodoBusca}
              onChange={e => setPeriodoBusca(Number(e.target.value))}
              className="ml-2 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
            >
              <option value={6}>6 meses (padrão)</option>
              <option value={9}>9 meses</option>
              <option value={12}>12 meses</option>
            </select>
          </label>

          <button
            onClick={() => setMostrarModalCalcular(true)}
            disabled={calculando}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {calculando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {calculando ? 'Calculando...' : 'Calcular / Recalcular agora'}
          </button>

          <button
            onClick={carregar}
            disabled={loading}
            title="Carrega só os produtos de rotatividade mensal do último cálculo (não roda o cálculo de novo, não mexe no ERP)"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <List size={13} />}
            Carregar calculados
          </button>

          <button
            onClick={handleExportar}
            disabled={exportando}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exportando ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Exportar .xlsx
          </button>

          <button
            onClick={handleEnviarAlertas}
            disabled={enviandoAlertas}
            title="Envia um email único com os produtos abaixo do estoque mínimo"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {enviandoAlertas ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
            Enviar Alertas
          </button>
        </div>
      )}

      {admin && (
        <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-gray-200 rounded-xl px-4 py-3">
          <label className="text-sm text-gray-600 flex flex-col gap-1">
            Pesquisar por Produto
            <input
              type="text"
              placeholder="Nome ou código do produto..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              className="w-64 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
            />
          </label>

          <button
            onClick={handleCalcularBusca}
            disabled={!busca.trim() || calculandoBusca}
            title="Calcula/recalcula só o produto do código digitado acima (não roda o cálculo de todos)"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-purple-300 bg-purple-50 text-purple-700 rounded-lg text-sm hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {calculandoBusca ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Calcular este produto
          </button>
        </div>
      )}

      {admin && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          A tela mostra apenas produtos de rotatividade <strong>mensal</strong> — carregar todos os grupos
          de uma vez trava o navegador. Produtos bimestrais e trimestrais só ficam disponíveis na
          planilha (.xlsx) exportada ou baixada ao final do cálculo.
        </p>
      )}

      {/* Tabela de produtos */}
      <div className="relative bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
        {calculando && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 backdrop-blur-sm">
            <Loader2 size={32} className="animate-spin text-blue-600" />
            <p className="text-sm font-medium text-gray-700">Calculando estoque mínimo/ideal...</p>
            <p className="text-xs text-gray-500">Isso pode levar alguns minutos. Aguarde, o resultado completo aparece ao final.</p>
          </div>
        )}

        {loading && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Carregando...</div>
        )}

        {erro && (
          <div className="px-5 py-4 text-sm text-red-600 border-b border-gray-100">{erro}</div>
        )}

        {!loading && linhasFiltradas.length === 0 && !erro && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            {linhas.length === 0
              ? 'Nenhum produto carregado. Clique em "Carregar calculados" para ver os produtos mensais do último resultado, ou em "Calcular / Recalcular agora" para gerar um novo.'
              : 'Nenhum produto encontrado para essa busca.'}
          </div>
        )}

        {!loading && linhasFiltradas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-medium">Produto</th>
                  <th className="text-left px-4 py-3 font-medium">GMM</th>
                  <th className="text-left px-4 py-3 font-medium">Grupo Sugerido</th>
                  <th className="text-left px-4 py-3 font-medium">Sem Histórico</th>
                  <th className="text-left px-4 py-3 font-medium">Prioridade Estratégica</th>
                  <th className="text-left px-4 py-3 font-medium">Grupo Final</th>
                  <th className="text-left px-4 py-3 font-medium">Lead Time (dias)</th>
                  <th className="text-left px-4 py-3 font-medium">Margem Segurança (dias)</th>
                  <th className="text-left px-4 py-3 font-medium">Estoque Mínimo</th>
                  <th className="text-left px-4 py-3 font-medium">Estoque Ideal</th>
                  {admin && <th className="text-left px-4 py-3 font-medium">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {linhasFiltradas.map(linha => (
                  <tr key={linha.codigo_produto} className="hover:bg-gray-50 transition-colors align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{linha.nome_produto || '—'}</div>
                      <div className="text-xs text-gray-400">Código {linha.codigo_produto}</div>
                      {!!linha.sem_historico && (
                        <span className="inline-block mt-1 mr-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5">
                          Novo — sem histórico
                        </span>
                      )}
                      {!!linha.sem_venda_3_meses && (
                        <span className="inline-block mt-1 text-xs bg-gray-100 text-gray-600 border border-gray-300 rounded px-1.5 py-0.5" title="Sem venda nos últimos 3 meses — não entra no alerta de compra">
                          Sem venda recente
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-gray-700">{linha.gmm != null ? Number(linha.gmm).toFixed(3) : '—'}</td>

                    <td className="px-4 py-3 text-gray-700">
                      {ROTULO_GRUPO[linha.grupo_sugerido] || '—'}
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <input
                          type="checkbox"
                          checked={!!valorAtual(linha, 'sem_historico')}
                          onChange={e => handleChange(linha.codigo_produto, 'sem_historico', e.target.checked)}
                        />
                      ) : (
                        <span>{linha.sem_historico ? 'Sim' : 'Não'}</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <input
                          type="checkbox"
                          checked={!!valorAtual(linha, 'prioridade_estrategica')}
                          onChange={e => handleChange(linha.codigo_produto, 'prioridade_estrategica', e.target.checked)}
                        />
                      ) : (
                        <span>{linha.prioridade_estrategica ? 'S' : 'N'}</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <select
                          value={valorAtual(linha, 'grupo_final') || ''}
                          onChange={e => handleChange(linha.codigo_produto, 'grupo_final', e.target.value)}
                          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                        >
                          <option value="">Automático (= sugerido)</option>
                          <option value="mensal">Mensal</option>
                          <option value="bimestral">Bimestral</option>
                          <option value="trimestral">Trimestral</option>
                        </select>
                      ) : (
                        <span>{ROTULO_GRUPO[linha.grupo_final] || '—'}</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <input
                          type="number"
                          min="0"
                          placeholder="Padrão"
                          className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                          value={valorAtual(linha, 'lead_time_dias') ?? ''}
                          onChange={e => handleChange(linha.codigo_produto, 'lead_time_dias', e.target.value)}
                        />
                      ) : (
                        <span>{linha.lead_time_dias ?? '—'}</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <input
                          type="number"
                          min="0"
                          placeholder="Padrão"
                          className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                          value={valorAtual(linha, 'margem_seguranca_dias') ?? ''}
                          onChange={e => handleChange(linha.codigo_produto, 'margem_seguranca_dias', e.target.value)}
                        />
                      ) : (
                        <span>{linha.margem_seguranca_dias ?? '—'}</span>
                      )}
                    </td>

                    <td className="px-4 py-3 font-medium text-gray-900">{linha.estoque_minimo_calculado ?? '—'}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{linha.estoque_ideal_calculado ?? '—'}</td>

                    {admin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSalvar(linha)}
                            disabled={!temAlteracao(linha) || salvandoCodigo === linha.codigo_produto}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {salvandoCodigo === linha.codigo_produto
                              ? <Loader2 size={13} className="animate-spin" />
                              : <Save size={13} />}
                            {sucessoCodigo === linha.codigo_produto ? 'Salvo!' : 'Salvar'}
                          </button>
                          <button
                            onClick={() => handleRecalcularProduto(linha)}
                            disabled={recalculandoCodigo === linha.codigo_produto}
                            title="Recalcular só este produto (usa o período de busca selecionado acima)"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {recalculandoCodigo === linha.codigo_produto
                              ? <Loader2 size={13} className="animate-spin" />
                              : <RefreshCw size={13} />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Parâmetros Globais */}
      {admin && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setMostrarParametros(v => !v)}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
          >
            Parâmetros Globais
            <span className="text-xs text-gray-400">{mostrarParametros ? 'ocultar' : 'mostrar'}</span>
          </button>

          {mostrarParametros && (
            <div className="px-5 py-4 border-t border-gray-100">
              {!parametros && <div className="text-sm text-gray-400">Carregando...</div>}

              {parametros && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    ['limite_giro_alto', 'Limite Giro Alto (un/mês)'],
                    ['limite_giro_medio', 'Limite Giro Médio (un/mês)'],
                    ['margem_seguranca_padrao_dias', 'Margem Segurança Padrão (dias)'],
                    ['lead_time_padrao_dias', 'Lead Time Padrão (dias)'],
                    ['ciclo_mensal_dias', 'Ciclo Mensal (dias)'],
                    ['ciclo_bimestral_dias', 'Ciclo Bimestral (dias)'],
                    ['ciclo_trimestral_dias', 'Ciclo Trimestral (dias)'],
                    ['meses_historico_gmm', 'Meses de Histórico do GMM'],
                    ['fator_reducao_trimestral', 'Fator Redução Trimestral (0-1)'],
                  ].map(([campo, rotulo]) => (
                    <label key={campo} className="text-xs text-gray-600">
                      {rotulo}
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max={campo === 'fator_reducao_trimestral' ? 1 : undefined}
                        className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                        value={valorParametro(campo)}
                        onChange={e => handleChangeParametro(campo, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              )}

              {parametros && (
                <div className="mt-4">
                  <button
                    onClick={handleSalvarParametros}
                    disabled={Object.keys(edicoesParametros).length === 0 || salvandoParametros}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {salvandoParametros ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Salvar Parâmetros
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de aviso antes de disparar o cálculo em lote */}
      {mostrarModalCalcular && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertTriangle size={18} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Cálculo pode demorar</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Como há muitos produtos, essa consulta pode levar de <strong>1 a 10 minutos</strong>,
                  dependendo da velocidade da conexão. A tela ficará ocupada até terminar. Ao final, a
                  planilha (.xlsx) com o resultado completo é baixada automaticamente — o resultado não
                  é exibido na tela para não travar o navegador.
                </p>
              </div>
            </div>

            {statusCalculo?.ultimoCalculo && (
              <div className={`rounded-lg px-3 py-2 mb-3 text-sm ${
                horasDesde(statusCalculo.ultimoCalculo) < HORAS_CALCULO_RECENTE
                  ? 'bg-amber-50 border border-amber-200 text-amber-800'
                  : 'bg-gray-50 border border-gray-200 text-gray-600'
              }`}>
                Último cálculo: <strong>{formatarDataHora(statusCalculo.ultimoCalculo)}</strong>
                {' '}({statusCalculo.totalProdutos} produto(s)).
                {horasDesde(statusCalculo.ultimoCalculo) < HORAS_CALCULO_RECENTE && (
                  <> Foi calculado há menos de {HORAS_CALCULO_RECENTE}h — talvez não seja necessário rodar de novo.</>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setMostrarModalCalcular(false)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCalcular}
                className="px-3 py-1.5 border border-blue-300 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Prosseguir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
